import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-dracula";
import { FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import AceEditor from "react-ace";
import {
  ConditionExpression,
  TOnboardingConfig,
  TVerificationAppFieldConfigItem,
} from "../../models";
import { validateConditionSchema } from "../AppSettingsSchema/conditionValidation";
import { useAppSettingsSchemaContext } from "../AppSettingsSchema/context";
import {
  ConditionValidationMode,
  validateConditionInContext,
} from "../AppSettingsSchema/logicValidation";
import { createSimpleCondition } from "./conditionHelpers";

const EDITOR_HEIGHT = "520px";

const ValidationOverlay: FC<{
  sections: {
    details: string[];
    title: string;
  }[];
}> = memo(({ sections }) => {
  if (sections.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        position: "absolute",
        top: 16,
        right: 16,
        p: 2,
        bgcolor: "#fdeded",
        color: "#5f2120",
        border: "1px solid #f5c6cb",
        maxWidth: "420px",
        maxHeight: "300px",
        overflowY: "auto",
        zIndex: 10,
      }}
    >
      <Stack spacing={1.25}>
        {sections.map(({ details, title }, sectionIndex) => (
          <Box
            key={title}
            sx={
              sectionIndex === 0
                ? undefined
                : { pt: 1.25, borderTop: "1px solid #f5c6cb" }
            }
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
              {title}
            </Typography>
            {details.map((detail, index) => (
              <Typography
                key={`${title}-${index}`}
                variant="body2"
                sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}
              >
                {detail}
              </Typography>
            ))}
          </Box>
        ))}
      </Stack>
    </Paper>
  );
});

export const ConditionEditorDialog: FC<{
  availableFields: TVerificationAppFieldConfigItem[];
  condition: ConditionExpression;
  fieldIndex?: number;
  open: boolean;
  stepIndex?: number;
  title: string;
  validationConfig: TOnboardingConfig;
  validationMode?: ConditionValidationMode;
  validationPath: string;
  onClose: () => void;
  onSave: (nextCondition: ConditionExpression) => void;
}> = memo(
  ({
    availableFields,
    condition,
    fieldIndex,
    open,
    stepIndex,
    title,
    validationConfig,
    validationMode,
    validationPath,
    onClose,
    onSave,
  }) => {
    const { conditionValidator } = useAppSettingsSchemaContext();
    const fallbackCondition = useMemo(
      () => createSimpleCondition(availableFields),
      [availableFields],
    );
    const [draftJson, setDraftJson] = useState(() =>
      JSON.stringify(condition ?? fallbackCondition, null, 2),
    );

    useEffect(() => {
      if (!open) {
        return;
      }

      setDraftJson(JSON.stringify(condition ?? fallbackCondition, null, 2));
    }, [condition, fallbackCondition, open]);

    const parsedCondition = useMemo(() => {
      try {
        return JSON.parse(draftJson) as ConditionExpression;
      } catch {
        return null;
      }
    }, [draftJson]);

    const jsonErrors = useMemo(() => {
      try {
        JSON.parse(draftJson);
        return [];
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        return [`Invalid JSON: ${message}`];
      }
    }, [draftJson]);

    const schemaErrors = useMemo(
      () =>
        jsonErrors.length > 0 || parsedCondition === null
          ? []
          : validateConditionSchema(conditionValidator, parsedCondition),
      [conditionValidator, jsonErrors.length, parsedCondition],
    );

    const logicErrors = useMemo(() => {
      if (
        jsonErrors.length > 0 ||
        schemaErrors.length > 0 ||
        parsedCondition === null
      ) {
        return [];
      }

      const validationErrors = validateConditionInContext({
        config: validationConfig,
        condition: parsedCondition,
        path: validationPath,
        stepIndex,
        fieldIndex,
        validationMode,
      });

      if (validationErrors.length === 0) {
        return [];
      }

      return [
        `${validationPath}:`,
        ...validationErrors.map((error) => `  - ${error.message}`),
      ];
    }, [
      fieldIndex,
      jsonErrors.length,
      parsedCondition,
      schemaErrors.length,
      stepIndex,
      validationConfig,
      validationMode,
      validationPath,
    ]);

    const validationSections = useMemo(
      () =>
        [
          { title: "JSON", details: jsonErrors },
          { title: "Schema", details: schemaErrors },
          { title: "Logic", details: logicErrors },
        ].filter(({ details }) => details.length > 0),
      [jsonErrors, logicErrors, schemaErrors],
    );

    const handleSave = useCallback(() => {
      if (
        parsedCondition === null ||
        jsonErrors.length > 0 ||
        schemaErrors.length > 0 ||
        logicErrors.length > 0
      ) {
        return;
      }

      onSave(parsedCondition);
    }, [
      jsonErrors.length,
      logicErrors.length,
      onSave,
      parsedCondition,
      schemaErrors.length,
    ]);

    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xl"
        disablePortal
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ position: "relative" }}>
            <AceEditor
              mode="json"
              theme="dracula"
              name="conditionJsonEditor"
              onChange={setDraftJson}
              value={draftJson}
              width="100%"
              height={EDITOR_HEIGHT}
              fontSize={14}
              setOptions={{
                showLineNumbers: true,
                tabSize: 2,
                useWorker: false,
              }}
            />
            <ValidationOverlay sections={validationSections} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save condition
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);
